<script setup>
import { ref, computed, watch, onMounted, onUnmounted, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { $router } from '@/router'
import { $bus, mqtt } from '@/common/events'
import { verbose } from '@/common/config'
import { dialog } from '@/common/dialog'
import { utc } from '@/common/helper'
import { hcu } from '@/common/hcu'
import { env } from '@/secrets/env'
import { reload } from '@/composables/app'
import { useConfig } from '@/composables/config'
import { useTitle } from '@/composables/title'
import { useAppState } from '@/composables/app'
import { useAuthState } from '@/composables/auth'
import { useRuntime } from '@/composables/runtime'
import { useVersion } from '@/composables/version'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useVirtualKeyboard } from '@/composables/keyboard'
import { useLinkTag, useScriptTag } from '@/composables/html'
import { useLanguage, useTranslate } from '@/composables/language'
import { useSettings, onSettingsChanged } from '@/composables/settings'
import { onSmartCardUnmounted } from '@/composables/hardware/smartcard'
import { setZoomFactor } from '@/composables/electron'
import { useScreensaver } from '@/composables/screensaver'
import { useMasterCabinet } from '@/composables/cabinet'
import { useCabinetState } from '@/composables/cabinet'
import { useCabinetStatus } from '@/composables/cabinet'
import { useSessionTimer } from '@/composables/session'
import { useInterrupt } from '@/composables/interrupt'
import { useSystemStatus } from '@/composables/system'
import { useWebSocketBus } from '@/composables/WebSocketBus'
import { useJsonRpc } from '@/composables/jsonrpc'
import { useTheme } from '@/composables/theme'
import { LogService } from '@/services/LogService.js'
import { AuthService } from '@/services/AuthService.js'
import { ConfigService } from '@/services/ConfigService.js'
import { CabinetService } from '@/services/CabinetService.js'
import TopBar from '@/components/TopBar.vue'
import SideMenu from '@/components/SideMenu.vue'
import DebugFooter from '@/components/Maintenance/DebugFooter.vue';
import SessionTimeout from '@/components/SessionTimeout.vue'
import VirtualKeyboard from '@/components/VirtualKeyboard.vue';
import Screensaver from '@/components/Screensaver/Screensaver.vue';
import Maintenance from '@/components/Maintenance/Maintenance.vue';
import CabinetAlarm from '@/components/Maintenance/CabinetAlarm.vue';
import ToastPlaceholder from '@/components/ToastPlaceholder.vue';
import MinResolutionRequired from '@/components/MinResolutionRequired.vue';
import ModalNotification from '@/components/Notification/ModalNotification.vue';

//Initialize services
const configService = new ConfigService();
const logService = new LogService();
const authService = new AuthService();
const cabinetService = new CabinetService();

//Initialize composables
const { t, setLanguage } = useTranslate();
const { settings } = useSettings();
const { appState } = useAppState();
const { uptime } = useRuntime();
const { isDark, enableDark, disableDark } = useTheme();
const { latestStatus } = useSystemStatus({ enabled: true });
const { menuVisible, menuSelected } = useMenu();
const { submenuVisible } = useSubmenu();
const { topbarVisible } = useTopBar();
const { vkeyboard } = useVirtualKeyboard();
const { masterCabinet, masterCabinetId } = useMasterCabinet();
const { allowInterruption, canPostponeAction } = useInterrupt();
const { appContext } = getCurrentInstance();
const authStore = useAuthState();
const route = useRoute();

const wsbus = appContext.config.globalProperties.$bus;

const isLoading = ref(true);
const isDebugPanel = ref(false);

const forceHideMenu = ref(false);

const isVirtualKeyboardInitialized = ref(false);

const userDisplayName = ref(null);

const cabinetInfo = ref(null);
const cabinetTitle = ref(null);

let delayedActionQueue = [ ];
let canInterruptEvent = true;

const useCase = ref(null);
const canSessionExpire = ref(true);
const promptForLogout = ref(false);

const sidemenu = ref(null);

const cabinetLocked = ref(false);
const lockedMessage = ref(null);
const criticalError = ref(null);

//const { enabled, timeout, theme } = useScreensaver();
const screensaverEnabled = ref(false);
const screensaverTimeout = ref(10000);
const screensaverTheme = ref("bounce");

//Preload required settings, language, color theme, etc.
const isReady = computed(() => isReadyOverride.value || appState.isReady);
const isReadyOverride = ref(false);
let readyTimeout = setTimeout(onReadyTimeout, 5000);

function onReadyTimeout() {
  console.log("[app] onReadyTimeout");
  $router.push({ name: "Index" });
  isReadyOverride.value = true;
}

async function onReadySuccess() {
  cabinetInfo.value = masterCabinet.value;
  cabinetTitle.value = masterCabinet.value?.name;

  //App version upgrade if any
  let upgraded = await upgrade();
  if (upgraded.reloading) {
    return;
  }

  applyRole();

  setTimeout(() => {
    isLoading.value = false;
    isVirtualKeyboardInitialized.value = true;

    try {
      //Needed to apply settings change - may display additional menu items
      sidemenu.value?.refreshButtons();
    }
    catch (err) {
    }

  }, 1000);

  logAppStart();

  $bus.emitQueued();

  /*
  const { wsbus } = useWebSocketBus({
    url: env.VITE_WSBUS_URL || "ws://localhost:3000/ws/v1/bus",
    token: env.VITE_WSBUS_TOKEN || "supersecret"
  });

  const { jsonrpc } = useJsonRpc({
    bus: wsbus
  });
  */

  /*
  wsbus.on('chat:message', (msg) => {
    console.log('Received:', msg);
  });

  wsbus.on('jsonrpc', (result) => {
    console.log("jsonrpc", result)
  });

  //Send message
  setTimeout(() => {
    wsbus.emit('chat:message', { text: 'Hello from Vue!' });
    wsbus.emit('jsonrpc', { jsonrpc: "2.0", method: "sayHello", params: [1, 2, 3], id: 1 });
  }, 2000);

  setTimeout(async () => {
    // let result = await jsonrpc.emit('sayHello', [1, 2, 5], { id: 2, wait: true });
    // console.log("jsonrpc result", result);
    let backend = new RPC(jsonrpc);
    let result = await backend.sayHello([2,3,4]);
    console.log("jsonrpc result", result);
  }, 1000);

  class RPC {
    constructor(jsonrpc) {
      this.jsonrpc = jsonrpc;
    }

    async sayHello(params) {
      return await this.jsonrpc.emit('sayHello', [1, 2, 5], { wait: true });
    }
  }
  */

  setTimeout(() => {
    //console.log("[ws] sayHello");
    //console.log("[ws] wsbus", jsonrpc.bus.connected, jsonrpc.bus.authenticated);
    // let remote = jsonrpc.methods();
    // remote.sayHello([1, 2, 5]);

    //Redefine resolving system status using JSON-RPC over WebSocket instead of REST API
    if (typeof jsonrpc != "undefined") {
      useSystemStatus({ enabled: true, jsonrpc });
    }
  }, 100)
}

watch(isReady, (value) => {
  if (readyTimeout) {
    clearTimeout(readyTimeout);
    readyTimeout = null;
    onReadySuccess();
  }
});

//Version migration
async function upgrade() {
  const result = { };

  try {
    //Compare versions
    const { version, lastVersion } = useVersion();
    const hasVersionChanged = (version.value && version.value != lastVersion.value);
    if (hasVersionChanged) {
      console.log("[app] Version changed", lastVersion.value, "=>", version.value);
    }

    if (!lastVersion.value) {
      let unusedKeys = [
        //"plt.cabinet.auth", //Keep, still in use
        //"plt.cabinet.version", //Keep
        "plt.cabinet.config",
        "plt.cabinet.cabinet",
        "plt.cabinet.cabinets",
        "plt.cabinet.menu",
        "plt.cabinet.state"
      ];

      let storages = [
        localStorage,
        sessionStorage,
        //cookieStorage
      ];

      for (let storage of storages) {
        for (let key of unusedKeys) {
          storage.removeItem(key);
        }
      }
    }

    //Apply last version
    useVersion({ lastVersion: version.value });

    //Clear cache and reload if version changed
    if (hasVersionChanged) {
      console.log("[app] Reloading...");
      reload({ clearCache: true });
      result.reloading = true;
    }
  }
  catch (err) {
    result.error = err;
    console.error("[app] upgrade error", err);
  }

  return result;
}

function initMessageQueue() {
  try {
    let verbose = import.meta.env.DEV;

    //Initialize two-way communication
    mqtt.init({ verbose: false });
    mqtt.on('connect', () => {
      if (verbose) {
        console.log("[mqtt] connected");
      }
    });
    mqtt.on('disconnect', () => {
      if (verbose) {
        console.log("[mqtt] disconnected");
      }
    });
    mqtt.on('received', (data) => {
      if (verbose) {
        //console.log("[mqtt] received", data.topic, data.payload);
      }
      if (data.topic == "hcu/cabinet/drum/debug") {
        try {
          if (data.payload.text && /^[01]{10,50}$/g.test(data.payload.text)) {
            let anyDoorOpen = (data.payload.text.indexOf("1") >= 0);
            useCabinetStatus({ anyDoorOpen });
          }
        }
        catch (err) {
        }
      }
      // if (data.topic == "hcu/barcode/scan") {
      //   $bus.emit("barcode received", data.payload);
      // }
    });
    mqtt.on('hcu/cabinet/drum/alarm', (data) => {
      if (verbose) {
        console.log("hcu/cabinet/drum/alarm", data);
      }
      $bus.emit("header message", { message: "Predal je odprt!" });
      mqtt.once('hcu/cabinet/drum/alarm/clear', (data) => {
        console.log("hcu/cabinet/drum/alarm/clear", data);
        $bus.emit("header message", { message: null });
      });
    });

    mqtt.on('hcu/program/reload/request', async (data) => {
      window.location.reload();
    });

    mqtt.on('hcu/program/lock/request', async (data) => {
      cabinetLocked.value = true;
      lockedMessage.value = data.message;
    });

    mqtt.on('hcu/program/unlock/request', async (data) => {
      cabinetLocked.value = false;
    });

    mqtt.on('hcu/program/error/request', async (data) => {
      criticalError.value = data.error;
    });

    mqtt.on('hcu/user/logout/request', async (data) => {
      console.log("log out request, can interrupt?", canInterruptEvent);

      let action = async () => {
        logout({ kick: true, reason: "Log out issued by external force" }).then(() => { }).catch(() => { });
      };

      if (canInterruptEvent && allowInterruption.value) {
        await action();  
      }
      else { //TODO: canDelayAction
        //Delayed action - when canInterrupt goes back to true perform log out
        delayedActionQueue.push(action);
      }
    });

  }
  catch (err) {
    console.error(err);
  }
}

//Initialize components based on data attribute selectors
onMounted(async () => {
  console.log("[app] App mounted", env.VITE_SEMVER, "+" + uptime() + "ms");

  useCase.value = env.VITE_USE_CASE;
  canSessionExpire.value = (useCase.value == "gloves" ? false : true);

  useTheme(localStorage.getItem('theme') || route.query?.theme);

  // if (route.query.language) {
  //   useLanguage(route.query.language);
  // }
  // if (route.query.timezone) {
  //   useTimeZone(route.query.timezone);
  // }
  
  useTitle({ prefix: "PLT", prefixSpacer: ": ", pageTitle: null, resolution: true });
  useMenu(false);
  useSubmenu(false);

  //Include FontAwesome icons
  useLinkTag("/lib/fontawesome-free-6.7.2-web/css/all.min.css");

  //Included here to prevent Vite build warnings
  useScriptTag("/socket.io/socket.io.js", initMessageQueue);

  //Barcode generator
  useScriptTag("/lib/jsbarcode/JsBarcode.all.min.js");

  if (isReady.value) {
    clearTimeout(readyTimeout);
    readyTimeout = null;
    onReadySuccess();
  }
});

async function logAppStart() {
  try {
    let cabinetId = masterCabinetId.value;
    if (appState.isLogEnabled) {
      await logService.insertLog({
        a: "cabinet.started",
        t: "cabinet",
        s: "info",
        cid: cabinetId,
        msg: "Zagon omare. Omara je pripravljena za uporabo.",
        //data: result,
        ts: new Date()
      });
    }
  }
  catch (err) {
    //Recording a start-up log is non-fatal — the control panel endpoint may be offline
    console.warn("[log] app ready (skipped)", err?.message || err);
  }
}

//Important activity
$bus.on("interrupt promise", async function(data) {
  let isChange = (canInterruptEvent != !!data.canInterrupt);

  canInterruptEvent = !!data.canInterrupt;
  console.log("[interrupt] enabled =", canInterruptEvent);

  //Avoid session timeout while a tray is open
  //useSessionTimer(true);

  //Resume session timeout - only if logged in
  //useSessionTimer(false);

  if (isChange && canInterruptEvent && allowInterruption.value) {
    //Perform delayed actions
    if (delayedActionQueue.length) {
      for (let action of delayedActionQueue) {
        await action();
      }
    }
  }
});

//Log out from neighbour cabinets
$bus.on("logout other", async function(data) {
  console.log("on logout other");
  mqtt.publish("hcu/user/logout-other/request", data);
});

function sendDebugVariables() {
  try {
    let data = {
      system: latestStatus.value,
      session: authStore.sessionId.value ? {
        sessionId: authStore.sessionId.value,
        userId: authStore.userId.value,
      } : null,
      logged: authStore.isLogged.value,
      route: route,
      /*
      {
        path: '/admin/components',
        name: 'AdminUserInterfaceTest',
        params: {},
        query: {},
        hash: '',
      }
      */
    };
    mqtt.publish("hcu/display/HDMI-A-1/debug", data);
  }
  catch (err) {
  }
}

watch(latestStatus, (value) => {
  //TODO: Deep inspect if changed
  //sendDebugVariables();
});

function applyRole() {
  userDisplayName.value = authStore.user.value?.displayName;
  //forceHideMenu.value = (authStore.role.value == "regular");
}

async function logout(options) {
  try {
    let sessionId = authStore.sessionId.value;
    let userId = authStore.userId;

    //Clear session
    authStore.logout(options);

    try {
      //Clear session on the server side
      await authService.logout({ sessionId });
    }
    catch (err) {
      if (appState.isLogEnabled) {
        logService.insertLog({
          a: "user.logout",
          t: "audit",
          s: "info",
          uid: userId,
          sid: sessionId,
          msg: "Odjava uporabnika iz omare",
          //data: result,
          ts: new Date()
        }).then(console.log).catch(console.error);
      }
    }

    configService.clearFlag("session").then(() => { }).catch(() => { });
  }
  catch (err) {
    console.error(err);
  }

  try {
    //Redirect to login page
    $router.push({ name: "Login" }); //TODO: toast with logout reason
  }
  catch (err) {
    console.error(err);
  }
}

//Apply changes
onSettingsChanged((settings) => {
  console.log("[app] Settings change detected");
  if (typeof settings == "object") {
    try {
      //Apply zoom level
      if (settings.display?.zoomFactor) {
        setZoomFactor(settings.display?.zoomFactor);
      }

      //Apply theme and color scheme
      if (settings.theme?.name) {
        useTheme(settings.theme?.name);
      }

      //Apply language
      if (settings.language) {
        setLanguage(settings.language);
      }

      //Virtual keyboard
      if (typeof settings.virtualKeyboard != "undefined" && vkeyboard) {
        vkeyboard.enabled = !!settings.virtualKeyboard;
        if (!vkeyboard.enabled /* && vkeyboard.visible */) {
          vkeyboard.hide();
        }
      }

      //Virtual keyboard: can enter chars at caret position (otherwise it will append at the end)
      if (typeof settings.debug?.vkcaret != "undefined" && vkeyboard) {
        const { canRepositionCaret } = useVirtualKeyboard();
        canRepositionCaret.value = (settings.debug?.vkcaret == true);
      }

      //Ask user to confirm log out
      promptForLogout.value = settings.session?.promptForLogout;

      //Show/hide debug panel
      isDebugPanel.value = settings.debug?.enabled;

      //Needed to apply settings change - may display additional menu items
      sidemenu.value?.refreshButtons();
    }
    catch (err) {
      console.error("[app] Settings changed", err);
    }
  }
});

onSmartCardUnmounted(async (data) => {
  console.log("smartcard removed, can interrupt?", canInterruptEvent, authStore.isLogged.value);

  if (!authStore.isLogged.value) {
    console.log("smartcard removed, not logged in anyway");
    return;
  }

  let action = async () => {
    let performLogout = false;

    if (settings.value.session?.promptForLogout) {
      //Prompt before log out
      let result = await dialog({
        type: "question-danger",
        title: "Kartica za dostop",
        text: "Kartica je vzeta iz čitalca.\r\nNadaljujem z odjavo iz omare?",
        confirmButtonText: "Odjavi",
        cancelButtonText: "Ohrani sejo",
      });
      
      if (result && result.confirm) {
        performLogout = true;
      }
    }
    else {
      performLogout = true;
    }

    if (performLogout) {
      //Log out without prompt
      logout({ userInitiated: true, reason: "Smart card removed" });
    }
  };

  if (canInterruptEvent && allowInterruption.value) {
    await action();  
  }
  else { //TODO: canDelayAction
    //Delayed action - when canInterrupt goes back to true perform log out
    delayedActionQueue.push(action);
  }
});

$router.beforeEach(async (to) => {
  //Hide keyboard on menu change or click
  if (vkeyboard && vkeyboard.visible) {
    vkeyboard.hide();
  }
});

$bus.on("login", (data) => {
  //userDisplayName.value = data?.displayName; //Must be hidden due to the GDPR
  //userDisplayName.value = data?.username; //Can be numeric if no consent given by GDPR
  //userDisplayName.value = authStore.user.value?.username; //Can be numeric if no consent given by GDPR
  console.log("login", data)
  mqtt.publish("hcu/user/login", { username: data.username });
});

//Route changed
watch(route, (newValue, oldValue) => {
  applyRole();
  sendDebugVariables();
});
</script>

<template>

  <!-- Preloading... -->
  <div class="loading-center" v-if="!isReady">
    <!-- <svg role="status" class="w-10 h-10 text-gray-200 dark:text-gray-600 fill-blue-600 animate-spin spinner" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin: 0px auto;"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"></path><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"></path></svg> -->
    <Spinner size="10" />
  </div>

  <!-- Ready -->
  <div class="w-full bg-gray-50 dark:bg-gray-800" v-if="isReady">
    <!-- Top menu bar -->
    <header class="topbar-position" :class="{ 'menu-visible': menuVisible, 'menu-invisible': !menuVisible }" aria-label="Top bar" v-if="topbarVisible">
      <TopBar :title="cabinetTitle" :user-display-name="userDisplayName" />
    </header>

    <!-- Side menu -->
    <aside class="menu-position" :class="{ 'menu-width': menuVisible, 'submenu-width': submenuVisible }" aria-label="Sidebar" v-if="menuVisible">
      <SideMenu ref="sidemenu" />
    </aside>

    <!-- Main content -->
    <div :class="{ 'menu-offset': menuVisible, 'submenu-offset': submenuVisible, 'topbar-offset': topbarVisible }">
      <!-- class="block w-full bg-gray-50 dark:bg-gray-900 p-5" -->
      <router-view />
    </div>

    <!-- Debug panel -->
    <DebugFooter :extended="true" v-if="isDebugPanel" />

    <!-- Virtual keyboard -->
    <!-- <VirtualKeyboard ref="vkeyboardElement" :enabled="true" :visible="false" /> -->
    <VirtualKeyboard />

    <!-- Placeholder reserved for toast messages -->
    <ToastPlaceholder />

    <!-- Minimum resolution required notice -->
    <!-- Commented due to new zoom level settings -->
    <!-- <MinResolutionRequired /> -->

    <!-- Alarm on cabinet -->
    <CabinetAlarm />

    <!-- Maintenance -->
    <Maintenance />

    <!-- Screensaver -->
    <Screensaver :enabled="screensaverEnabled" :timeout="screensaverTimeout" :theme="screensaverTheme" />

    <!-- Timer for user's login session -->
    <SessionTimeout />

    <!-- Notification after user's login -->
    <ModalNotification />
  </div>
</template>

<style scoped>
div.loading-center {
  user-select: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

div, p, a, input, button {
  outline: none !important;
  user-select: none !important;
}

.always-on-top { 
  display: block;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 120;
  background: white;
}

.menu-position {
  /* @apply fixed top-0 left-0 z-40 h-screen transition-transform -translate-x-full sm:translate-x-0; */ /* Hide on small screens */
  @apply fixed top-0 left-0 z-40 h-screen; /* Visible on all display sizes */
}

.menu-width {
  @apply w-16;
}

.menu-width.submenu-width {
  width: 320px;
}

.menu-offset {
  @apply pl-16;
}

.menu-offset.submenu-offset {
  padding-left: 320px;
}

.topbar-offset {
  @apply pt-16;
}

.topbar-position {
  @apply fixed top-0 z-20 w-full h-16;
}

.topbar-position.menu-invisible {
  @apply left-0;
}

.topbar-position.menu-visible {
  @apply left-16 pr-16;
}
</style>
